import ReactECharts from 'echarts-for-react';
import React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Tooltip } from 'react-daisyui';
import { LuBot } from 'react-icons/lu';
import { Link, useParams } from 'react-router-dom';

import type { UserStatSingleV2 } from '../../entities/analytics-v2';
import { getProviderCapabilities } from '../../entities/provider';
import { getUserStatsV2 } from '../../shared/api/dashboard';
import { useAIChat } from '../../shared/contexts/AIChat.context';
import { useSetDashboardHeader } from '../../shared/contexts/DashboardHeader.context';
import { useDateRange } from '../../shared/contexts/DateRange.provider';
import { useProjectById } from '../../shared/contexts/Projects.provider';
import { formatRiskFlags } from '../../shared/utils/risk-flag-labels';
import { asISODate, clamp, fmt1, fmtDaysFromHours, fmtPct, safeNum } from '../../shared/utils/stat-helpers';
import { InfoTip, KpiCard, MiniBarChart, StatEmpty, StatError, StatLoading } from '../../widgets/stats';

type MetricKey = 'completed' | 'reopens' | 'reopenRate' | 'cycle';

export const UserStatsPage: React.FC = () => {
  const { projectId, accountId } = useParams();

  const dateRangeCtx = useDateRange();
  const setDashHeader = useSetDashboardHeader();
  const { openChat } = useAIChat();
  const project = useProjectById(projectId);
  const supportsUsers = getProviderCapabilities(project?.provider).supportsUsers;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [stat, setStat] = useState<UserStatSingleV2 | null>(null);
  const [selectedKey, setSelectedKey] = useState<MetricKey>('completed');

  useEffect(() => {
    const run = async () => {
      if (!projectId || !accountId || !supportsUsers) {
        setLoading(false);
        return;
      }
      if (!dateRangeCtx?.from || !dateRangeCtx?.to) return;

      try {
        setLoading(true);
        setError(null);

        const range = { from: asISODate(dateRangeCtx.from), to: asISODate(dateRangeCtx.to) };
        const { stat } = await getUserStatsV2(projectId, accountId, range);
        setStat(stat ?? null);
      } catch (e) {
        console.error(e);
        setError('Failed to load user stats.');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [projectId, accountId, dateRangeCtx?.from, dateRangeCtx?.to, supportsUsers]);

  const headerRange = useMemo(() => {
    if (!stat) return '';
    return `${new Date(dateRangeCtx?.from || '').toLocaleDateString()} → ${new Date(dateRangeCtx?.to || '').toLocaleDateString()}`;
  }, [dateRangeCtx?.from, dateRangeCtx?.to, stat]);

  const kpis = useMemo(() => {
    if (!stat) {
      return { completed: 0, reopenRate: 0, cycleDays: 0, flowEff: 0 };
    }
    const s = stat.summary;
    return {
      completed: safeNum(s.completedCount),
      reopenRate: safeNum(s.reopenRatePercent),
      cycleDays: safeNum(s.avgCycleTimeHours) / 24,
      flowEff: safeNum(s.flowEfficiencyPercent),
    };
  }, [stat]);

  const caps = useMemo(
    () => ({
      reopen: 60,
      cycle: Math.max(10, kpis.cycleDays * 1.6 || 10),
    }),
    [kpis.cycleDays],
  );

  const timelineOption = useMemo(() => {
    const series = stat?.series ?? [];
    const labels = series.map((p) => {
      const d = new Date(p.day);
      return isNaN(d.getTime()) ? String(p.day) : d.toLocaleDateString();
    });

    const completedS = series.map((p) => safeNum(p.completedCount));
    const reopensS = series.map((p) => safeNum(p.reopenEvents));

    const pick = () => {
      switch (selectedKey) {
        case 'completed':
          return { name: 'Completed', type: 'bar' as const, data: completedS, suffix: '' };
        case 'reopens':
          return { name: 'Reopens', type: 'bar' as const, data: reopensS, suffix: '' };
        case 'reopenRate':
          return {
            name: 'Reopen rate',
            type: 'line' as const,
            data: series.map((p) => {
              const c = safeNum(p.completedCount);
              const r = safeNum(p.reopenEvents);
              return c > 0 ? Math.round((r / c) * 100) : 0;
            }),
            suffix: '%',
          };
        case 'cycle':
          return {
            name: 'Avg cycle (range)',
            type: 'line' as const,
            data: series.map(() => fmt1(kpis.cycleDays)),
            suffix: 'd',
          };
      }
    };

    const s = pick();

    return {
      tooltip: { trigger: 'axis' },
      grid: { left: 24, right: 16, top: 16, bottom: 52, containLabel: true },
      xAxis: { type: 'category', data: labels, axisLabel: { rotate: 18, interval: 'auto' } },
      yAxis: { type: 'value' },
      series: [
        {
          name: s.name,
          type: s.type,
          data: s.data,
          smooth: s.type === 'line',
          symbolSize: 6,
          areaStyle: s.type === 'line' ? { opacity: 0.08 } : undefined,
          barWidth: s.type === 'bar' ? 10 : undefined,
        },
      ],
    };
  }, [stat?.series, selectedKey, kpis.cycleDays]);

  const scoreBadges = useMemo(() => {
    const flags: { label: string; cls: string }[] = [];

    if (kpis.reopenRate >= 20) flags.push({ label: 'Quality risk', cls: 'badge-warning' });
    if (kpis.cycleDays >= 14) flags.push({ label: 'Slow flow', cls: 'badge-warning' });

    if (!flags.length) flags.push({ label: 'Looks ok', cls: 'badge-success' });

    return flags;
  }, [kpis.reopenRate, kpis.cycleDays]);

  useEffect(() => {
    setDashHeader({
      component: 'user',
      breadcrumb: (
        <Link to={`/dashboard/projects/${projectId}/users`} className="btn btn-sm btn-ghost px-0 h-auto min-h-0">
          <Tooltip message={'Users'} position={'bottom'}>
            <span className="text-xl">←</span>
          </Tooltip>
        </Link>
      ),
      title: stat ? (
        <span className="flex items-center gap-2 flex-wrap">
          {'User'}
          <span className="badge badge-ghost">{stat?.aiInsightPayload?.userId ?? accountId}</span>
          {scoreBadges.map((b) => (
            <span key={b.label} className={`badge ${b.cls}`}>
              {b.label}
            </span>
          ))}
        </span>
      ) : (
        'User'
      ),
      subtitle: stat ? (
        <span>
          Range: <span className="font-medium">{headerRange}</span>
        </span>
      ) : undefined,
      actions: stat ? (
        <button
          className="btn btn-primary btn-sm shadow-glow gap-1.5"
          onClick={() =>
            openChat({
              projectId,
              suggestedPrompts: [
                "Summarize this user's performance",
                ...(kpis.reopenRate > 15 ? ['Why does this user have a high reopen rate?'] : []),
                ...(kpis.cycleDays > 10 ? ['Why is the cycle time high for this user?'] : []),
                'What can this user improve?',
              ],
              insightText: (stat as any).aiInsightPayload?.notes?.riskFlags?.length
                ? formatRiskFlags((stat as any).aiInsightPayload.notes.riskFlags)
                : undefined,
              analyticsContext: {
                scope: 'user',
                projectIds: projectId ? [projectId] : undefined,
                targetUserIds: accountId ? [accountId] : undefined,
                dateRange:
                  dateRangeCtx?.from && dateRangeCtx?.to
                    ? {
                        from: dateRangeCtx.from.toISOString().split('T')[0],
                        to: dateRangeCtx.to.toISOString().split('T')[0],
                      }
                    : undefined,
              },
            })
          }
        >
          <LuBot size={14} />
          Ask mFlow
        </button>
      ) : undefined,
    });
    return () => setDashHeader({});
  }, [
    accountId,
    dateRangeCtx?.from,
    dateRangeCtx?.to,
    stat,
    scoreBadges,
    headerRange,
    projectId,
    setDashHeader,
    openChat,
    kpis,
  ]);

  if (loading) return <StatLoading />;
  if (!supportsUsers) return <StatEmpty message="Per-contributor stats aren't available for this provider yet." />;
  if (error) return <StatError message={error} />;
  if (!stat) return <StatEmpty message="No user stats." />;

  return (
    <div className="min-h-screen bg-base-200">
      <div className="p-4 space-y-4">
        {/* KPI strip */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard
            label={
              <>
                Completed <InfoTip text={'Completed tasks in selected range.'} />
              </>
            }
            value={kpis.completed}
            footer="Output"
          />

          <KpiCard
            label={
              <>
                Reopen rate
                <InfoTip text={'Reopen rate = reopenEvents / completedCount.\nLower is better.'} />
              </>
            }
            value={fmtPct(kpis.reopenRate)}
          >
            <MiniBarChart label="Reopen" value={clamp(kpis.reopenRate, 0, caps.reopen)} max={caps.reopen} suffix="%" />
          </KpiCard>

          <KpiCard
            label={
              <>
                Cycle time
                <InfoTip text={'Avg cycle time = createdAt → doneAt. Lower is better.'} />
              </>
            }
            footer="Avg across completed tasks"
          >
            <div className="text-3xl font-semibold mt-1">{fmtDaysFromHours(stat.summary.avgCycleTimeHours)}</div>
            <MiniBarChart
              label="Cycle"
              value={clamp(kpis.cycleDays, 0, caps.cycle)}
              max={caps.cycle}
              suffix="d"
              height="40px"
            />
          </KpiCard>

          <KpiCard
            label={
              <>
                Flow efficiency
                <InfoTip
                  text={
                    'Time in active statuses / total cycle time (project-level proxy, not per-user).\nIndustry avg: 15–40%. < 15% = lots of waiting. > 60% = very healthy.'
                  }
                />
              </>
            }
            footer="Project-level proxy"
          >
            <div
              className={`text-3xl font-semibold ${kpis.flowEff > 60 ? 'text-success' : kpis.flowEff > 0 && kpis.flowEff < 15 ? 'text-warning' : ''}`}
            >
              {kpis.flowEff > 0 ? fmtPct(kpis.flowEff) : '—'}
            </div>
            {kpis.flowEff > 0 && (
              <MiniBarChart label="Flow eff." value={clamp(kpis.flowEff, 0, 100)} max={100} suffix="%" height="40px" />
            )}
          </KpiCard>
        </div>

        {/* Timeline */}
        <div className="card bg-base-100 shadow-card">
          <div className="card-body p-4">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="text-lg font-semibold flex items-center">
                  Trend
                  <InfoTip text={'Daily series inside selected range.\nSwitch metric to inspect user behavior.'} />
                </div>
                <div className="text-sm opacity-70">Daily series</div>
              </div>

              <div className="join">
                {(
                  [
                    ['completed', 'Completed'],
                    ['reopens', 'Reopens'],
                    ['reopenRate', 'Reopen %'],
                    ['cycle', 'Cycle (range)'],
                  ] as const
                ).map(([k, label]) => (
                  <button
                    key={k}
                    className={`btn btn-sm join-item ${selectedKey === k ? 'btn-active' : ''}`}
                    onClick={() => setSelectedKey(k)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="h-[360px] mt-2">
              <ReactECharts option={timelineOption} style={{ height: '100%' }} />
            </div>

            <div className="text-xs opacity-60 mt-2">
              Note: cycle metric shows the range-level average as a constant line until per-day snapshots are added.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
